import type { ApiClient } from './client';
import { confirmPhotoUpload } from '../photoSafetyConsent';

export const imagesMethods = {
async getVehicleImages(vehicleId: number): Promise<any[]> {
      const response = await this.makeRequest<any[]>(`/images_list.php?vehicle_id=${vehicleId}`);
      return response.data || [];
    },


    async uploadVehicleImage(
      vehicleId: number,
      file: File,
      imageType: 'hero' | 'gallery' | 'modification',
      modificationId?: number,
      slotPosition?: number,
      labels?: { title?: string; altText?: string },
      focal?: { x: number; y: number; zoom?: number }
    ): Promise<any> {
      this.validateImageFile(file);
      const generation = this.sessionGeneration;

      const formData = new FormData();
      formData.append('image', file);
      formData.append('vehicle_id', vehicleId.toString());
      formData.append('image_type', imageType);
      if (modificationId) {
        formData.append('modification_id', modificationId.toString());
      }
      if (slotPosition !== undefined) {
        formData.append('slot_position', slotPosition.toString());
      }
      if (labels?.title) {
        formData.append('title', labels.title);
      }
      if (labels?.altText) {
        formData.append('alt_text', labels.altText);
      }
      if (focal) {
        formData.append('focal_x', String(focal.x));
        formData.append('focal_y', String(focal.y));
        formData.append('zoom', String(focal.zoom ?? 1));
      }

      await confirmPhotoUpload(formData);
      if (generation !== this.sessionGeneration) throw new Error('Session changed');
      // For FormData uploads, we need to let the browser set the Content-Type header
      // with the proper boundary, so we explicitly remove it from our headers
      const headers: HeadersInit = {};
      if (this.authTokens?.accessToken) {
        headers['Authorization'] = `Bearer ${this.authTokens.accessToken}`;
        if (this.isProductionHost()) {
          headers['X-Auth-Token'] = this.authTokens.accessToken;
        }
      }

      const url = `${this.baseUrl}/images_upload.php`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData
        });

        const result = await this.handleResponse<any>(response);

        if (result.success && result.data) {
          return result.data;
        }
        throw new Error(result.error || 'Failed to upload image');
      } catch (error) {
        console.error(`API request failed: /images_upload.php`, error);
        throw error;
      }
    },


    async deleteVehicleImage(imageId: number): Promise<void> {
      const response = await this.makeRequest<any>(`/images_delete.php?id=${imageId}`, {
        method: 'DELETE',
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete image');
      }
    },


    async updateVehicleImage(imageId: number, imageData: {
      title?: string;
      alt_text?: string;
      sort_order?: number;
      focal_x?: number;
      focal_y?: number;
      zoom?: number;
    }): Promise<any> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const response = await this.makeRequest<any>(`/images_update.php?id=${imageId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${this.getAccessToken()}`
          },
          body: JSON.stringify(imageData)
        });

        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to update image');
        }
      } catch (error: any) {
        console.error('Failed to update image:', error);
        throw new Error(error.message || 'Failed to update image');
      }
    },


    async reorderVehicleImages(vehicleId: number, imageOrders: Array<{ id: number, sort_order: number }>): Promise<void> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const response = await this.makeRequest<any>('/images_reorder.php', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAccessToken()}`
          },
          body: JSON.stringify({
            vehicle_id: vehicleId,
            image_orders: imageOrders
          }),
        });

        if (!response.success) {
          throw new Error(response.error || 'Failed to reorder images');
        }
      } catch (error: any) {
        console.error('Failed to reorder images:', error);
        throw new Error(error.message || 'Failed to reorder images');
      }
    },


    async updateVehicleSettings(vehicleId: number, settings: { show_pricing: boolean, show_performance: boolean }): Promise<void> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const requestData = {
          vehicle_id: vehicleId,
          show_pricing: settings.show_pricing,
          show_performance: settings.show_performance
        };

        // Use the real endpoint
        const response = await this.makeRequest<any>('/vehicle_settings_update.php', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAccessToken()}`
          },
          body: JSON.stringify(requestData),
        });

        if (!response.success) {
          throw new Error(response.error || 'Failed to update vehicle settings');
        }
      } catch (error: any) {
        console.error('Failed to update vehicle settings:', error);
        throw new Error(error.message || 'Failed to update vehicle settings');
      }
    }
} satisfies ThisType<ApiClient>;
